import { useEffect, useState } from "react";
import { SideDrawerShell } from "../../common/components";
import { productsApi } from "../api/productsApi";

const EMPTY = { name: "", description: "" };

export default function ProductDrawer({ mode = "create", product, onClose, onSaved, showToast, isAdmin }) {
  const isEdit = mode === "edit";

  const [form, setForm]     = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError]   = useState("");

  useEffect(() => {
    setForm(isEdit && product
      ? { name: product.name || "", description: product.description || "" }
      : EMPTY
    );
    setError("");
  }, [isEdit, product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  async function handleSave() {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setError("");
    setSaving(true);
    try {
      if (isEdit) {
        await productsApi.update(product.id, { name: form.name.trim(), description: form.description.trim() });
        showToast?.("Product updated");
      } else {
        await productsApi.create({ name: form.name.trim(), description: form.description.trim() });
        showToast?.("Product created");
      }
      onSaved?.();
    } catch (err) {
      showToast?.(err?.message || "Failed to save product", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete product "${product?.name}"? Features linked to it will lose their product reference.`)) return;
    setDeleting(true);
    try {
      await productsApi.delete(product.id);
      showToast?.("Product deleted");
      onSaved?.();
    } catch (err) {
      showToast?.(err?.message || "Failed to delete product", "error");
    } finally {
      setDeleting(false);
    }
  }

  const footer = (
    <div className="v2drw-foot-actions">
      {isEdit && isAdmin && (
        <button
          type="button"
          className="ftv2-product-delete-btn"
          onClick={handleDelete}
          disabled={saving || deleting}
        >
          {deleting ? "Deleting…" : "Delete"}
        </button>
      )}
      <span className="v2drw-foot-spacer" />
      <button type="button" className="v2drw-btn-cancel" onClick={onClose} disabled={saving || deleting}>
        Cancel
      </button>
      <button type="button" className="v2-btn-primary" onClick={handleSave} disabled={saving || deleting}>
        {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Product"}
      </button>
    </div>
  );

  return (
    <SideDrawerShell
      open
      subtitle="Products"
      title={isEdit ? (product?.name || "Edit Product") : "New Product"}
      onClose={onClose}
      width="min(480px, 100vw)"
      footer={footer}
    >
      <div className="ftv2-form">
        {error && <div className="ftv2-drawer-error">{error}</div>}

        <div className="ftv2-form-fld">
          <label className="ftv2-form-label">
            Name <span className="ftv2-label-hint">REQUIRED</span>
          </label>
          <input
            type="text"
            className="ftv2-form-input"
            value={form.name}
            onChange={(e) => { set("name", e.target.value); setError(""); }}
            placeholder="e.g. Trading Platform"
            autoFocus
          />
        </div>

        <div className="ftv2-form-fld">
          <label className="ftv2-form-label">Description</label>
          <textarea
            className="ftv2-form-textarea"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Optional — brief description of what this product covers"
            rows={4}
          />
        </div>
      </div>
    </SideDrawerShell>
  );
}
